/**
 * Generated by orval v7.21.0 🍺
 * Do not edit manually.
 * MovingWorlds API
 * OpenAPI spec version: 0.1.0
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type {
  CohortBaseDetail,
  GetCurrentUserCurrentCohort200,
  GetSimpleCohortsResponse,
  HTTPValidationError,
  MyCohortPreviewResponse,
  MyCohortResponse,
  NotificationRequestResponse,
  UserRejoinDataResponse,
} from './platformapi.schemas';

import getCohortMutator from '../custom-axios-instance';
import getCohortsMutator from '../custom-axios-instance';
import getCurrentUserCurrentCohortMutator from '../custom-axios-instance';
import getUserMyCohortDataMutator from '../custom-axios-instance';
import addNotificationRequestMutator from '../custom-axios-instance';
import getUserNotificationRequestsMutator from '../custom-axios-instance';
import getUserMyCohortPreviewMutator from '../custom-axios-instance';
import getUserRejoinDataMutator from '../custom-axios-instance';

/**
 * Retrieves detailed information for a cohort by its ID. Includes pertinent dates and derived progress milestone dates for the cohort.
 * @summary Get Cohort
 */
export const getCohort = (cohortId: number, signal?: AbortSignal) => {
  return getCohortMutator<CohortBaseDetail>({
    url: `${import.meta.env['VITE_API_HOST']}/cohorts/cohort/${cohortId}`,
    method: 'GET',
    signal,
  });
};

export const getGetCohortQueryKey = (cohortId?: number) => {
  return [`${import.meta.env['VITE_API_HOST']}/cohorts/cohort/${cohortId}`] as const;
};

export const getGetCohortQueryOptions = <
  TData = Awaited<ReturnType<typeof getCohort>>,
  TError = void | HTTPValidationError,
>(
  cohortId: number,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCohort>>, TError, TData>>;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetCohortQueryKey(cohortId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getCohort>>> = ({ signal }) =>
    getCohort(cohortId, signal);

  return { queryKey, queryFn, enabled: !!cohortId, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getCohort>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetCohortQueryResult = NonNullable<Awaited<ReturnType<typeof getCohort>>>;
export type GetCohortQueryError = void | HTTPValidationError;

export function useGetCohort<
  TData = Awaited<ReturnType<typeof getCohort>>,
  TError = void | HTTPValidationError,
>(
  cohortId: number,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCohort>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCohort>>,
          TError,
          Awaited<ReturnType<typeof getCohort>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCohort<
  TData = Awaited<ReturnType<typeof getCohort>>,
  TError = void | HTTPValidationError,
>(
  cohortId: number,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCohort>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCohort>>,
          TError,
          Awaited<ReturnType<typeof getCohort>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCohort<
  TData = Awaited<ReturnType<typeof getCohort>>,
  TError = void | HTTPValidationError,
>(
  cohortId: number,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCohort>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Cohort
 */

export function useGetCohort<
  TData = Awaited<ReturnType<typeof getCohort>>,
  TError = void | HTTPValidationError,
>(
  cohortId: number,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCohort>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetCohortQueryOptions(cohortId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Retrieves name and id for all cohorts - excludes legacy MWI cohorts or any not linked to corporate clients. Generally used to support UI dropdowns for cohort selection.
 * @summary Get Cohorts
 */
export const getCohorts = (signal?: AbortSignal) => {
  return getCohortsMutator<GetSimpleCohortsResponse>({
    url: `${import.meta.env['VITE_API_HOST']}/cohorts/cohorts`,
    method: 'GET',
    signal,
  });
};

export const getGetCohortsQueryKey = () => {
  return [`${import.meta.env['VITE_API_HOST']}/cohorts/cohorts`] as const;
};

export const getGetCohortsQueryOptions = <
  TData = Awaited<ReturnType<typeof getCohorts>>,
  TError = void,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCohorts>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetCohortsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getCohorts>>> = ({ signal }) =>
    getCohorts(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getCohorts>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetCohortsQueryResult = NonNullable<Awaited<ReturnType<typeof getCohorts>>>;
export type GetCohortsQueryError = void;

export function useGetCohorts<TData = Awaited<ReturnType<typeof getCohorts>>, TError = void>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCohorts>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCohorts>>,
          TError,
          Awaited<ReturnType<typeof getCohorts>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCohorts<TData = Awaited<ReturnType<typeof getCohorts>>, TError = void>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCohorts>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCohorts>>,
          TError,
          Awaited<ReturnType<typeof getCohorts>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCohorts<TData = Awaited<ReturnType<typeof getCohorts>>, TError = void>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCohorts>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Cohorts
 */

export function useGetCohorts<TData = Awaited<ReturnType<typeof getCohorts>>, TError = void>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCohorts>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetCohortsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Retrieves the current cohort for a given user
 * @summary Get Current User Current Cohort
 */
export const getCurrentUserCurrentCohort = (signal?: AbortSignal) => {
  return getCurrentUserCurrentCohortMutator<GetCurrentUserCurrentCohort200>({
    url: `${import.meta.env['VITE_API_HOST']}/cohorts/current`,
    method: 'GET',
    signal,
  });
};

export const getGetCurrentUserCurrentCohortQueryKey = () => {
  return [`${import.meta.env['VITE_API_HOST']}/cohorts/current`] as const;
};

export const getGetCurrentUserCurrentCohortQueryOptions = <
  TData = Awaited<ReturnType<typeof getCurrentUserCurrentCohort>>,
  TError = void | HTTPValidationError,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof getCurrentUserCurrentCohort>>, TError, TData>
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetCurrentUserCurrentCohortQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getCurrentUserCurrentCohort>>> = ({
    signal,
  }) => getCurrentUserCurrentCohort(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getCurrentUserCurrentCohort>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetCurrentUserCurrentCohortQueryResult = NonNullable<
  Awaited<ReturnType<typeof getCurrentUserCurrentCohort>>
>;
export type GetCurrentUserCurrentCohortQueryError = void | HTTPValidationError;

export function useGetCurrentUserCurrentCohort<
  TData = Awaited<ReturnType<typeof getCurrentUserCurrentCohort>>,
  TError = void | HTTPValidationError,
>(
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getCurrentUserCurrentCohort>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCurrentUserCurrentCohort>>,
          TError,
          Awaited<ReturnType<typeof getCurrentUserCurrentCohort>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCurrentUserCurrentCohort<
  TData = Awaited<ReturnType<typeof getCurrentUserCurrentCohort>>,
  TError = void | HTTPValidationError,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getCurrentUserCurrentCohort>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCurrentUserCurrentCohort>>,
          TError,
          Awaited<ReturnType<typeof getCurrentUserCurrentCohort>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCurrentUserCurrentCohort<
  TData = Awaited<ReturnType<typeof getCurrentUserCurrentCohort>>,
  TError = void | HTTPValidationError,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getCurrentUserCurrentCohort>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Current User Current Cohort
 */

export function useGetCurrentUserCurrentCohort<
  TData = Awaited<ReturnType<typeof getCurrentUserCurrentCohort>>,
  TError = void | HTTPValidationError,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getCurrentUserCurrentCohort>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetCurrentUserCurrentCohortQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Get User My Cohort Data
 */
export const getUserMyCohortData = (signal?: AbortSignal) => {
  return getUserMyCohortDataMutator<MyCohortResponse>({
    url: `${import.meta.env['VITE_API_HOST']}/cohorts/my-cohort`,
    method: 'GET',
    signal,
  });
};

export const getGetUserMyCohortDataQueryKey = () => {
  return [`${import.meta.env['VITE_API_HOST']}/cohorts/my-cohort`] as const;
};

export const getGetUserMyCohortDataQueryOptions = <
  TData = Awaited<ReturnType<typeof getUserMyCohortData>>,
  TError = void | HTTPValidationError,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserMyCohortData>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetUserMyCohortDataQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getUserMyCohortData>>> = ({ signal }) =>
    getUserMyCohortData(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getUserMyCohortData>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetUserMyCohortDataQueryResult = NonNullable<
  Awaited<ReturnType<typeof getUserMyCohortData>>
>;
export type GetUserMyCohortDataQueryError = void | HTTPValidationError;

export function useGetUserMyCohortData<
  TData = Awaited<ReturnType<typeof getUserMyCohortData>>,
  TError = void | HTTPValidationError,
>(
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUserMyCohortData>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUserMyCohortData>>,
          TError,
          Awaited<ReturnType<typeof getUserMyCohortData>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUserMyCohortData<
  TData = Awaited<ReturnType<typeof getUserMyCohortData>>,
  TError = void | HTTPValidationError,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUserMyCohortData>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUserMyCohortData>>,
          TError,
          Awaited<ReturnType<typeof getUserMyCohortData>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUserMyCohortData<
  TData = Awaited<ReturnType<typeof getUserMyCohortData>>,
  TError = void | HTTPValidationError,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUserMyCohortData>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get User My Cohort Data
 */

export function useGetUserMyCohortData<
  TData = Awaited<ReturnType<typeof getUserMyCohortData>>,
  TError = void | HTTPValidationError,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUserMyCohortData>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetUserMyCohortDataQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Adds a record on CohortNotificationRequests to indicate the user has requested notification for upcoming cohorts.
 * @summary Add Notification Request
 */
export const addNotificationRequest = (signal?: AbortSignal) => {
  return addNotificationRequestMutator<unknown>({
    url: `${import.meta.env['VITE_API_HOST']}/cohorts/notification-request`,
    method: 'POST',
    signal,
  });
};

export const getAddNotificationRequestMutationOptions = <
  TError = void | HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof addNotificationRequest>>,
    TError,
    void,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof addNotificationRequest>>,
  TError,
  void,
  TContext
> => {
  const mutationKey = ['addNotificationRequest'];
  const { mutation: mutationOptions } =
    options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
        options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof addNotificationRequest>>,
    void
  > = () => {
    return addNotificationRequest();
  };

  return { mutationFn, ...mutationOptions };
};

export type AddNotificationRequestMutationResult = NonNullable<
  Awaited<ReturnType<typeof addNotificationRequest>>
>;

export type AddNotificationRequestMutationError = void | HTTPValidationError;

/**
 * @summary Add Notification Request
 */
export const useAddNotificationRequest = <TError = void | HTTPValidationError, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof addNotificationRequest>>,
      TError,
      void,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof addNotificationRequest>>,
  TError,
  void,
  TContext
> => {
  const mutationOptions = getAddNotificationRequestMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Retrieves the list of cohort IDs that the user has requested notifications for.
 * @summary Get User Notification Requests
 */
export const getUserNotificationRequests = (signal?: AbortSignal) => {
  return getUserNotificationRequestsMutator<NotificationRequestResponse>({
    url: `${import.meta.env['VITE_API_HOST']}/cohorts/notification-requests`,
    method: 'GET',
    signal,
  });
};

export const getGetUserNotificationRequestsQueryKey = () => {
  return [`${import.meta.env['VITE_API_HOST']}/cohorts/notification-requests`] as const;
};

export const getGetUserNotificationRequestsQueryOptions = <
  TData = Awaited<ReturnType<typeof getUserNotificationRequests>>,
  TError = void | HTTPValidationError,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof getUserNotificationRequests>>, TError, TData>
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetUserNotificationRequestsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getUserNotificationRequests>>> = ({
    signal,
  }) => getUserNotificationRequests(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getUserNotificationRequests>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetUserNotificationRequestsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getUserNotificationRequests>>
>;
export type GetUserNotificationRequestsQueryError = void | HTTPValidationError;

export function useGetUserNotificationRequests<
  TData = Awaited<ReturnType<typeof getUserNotificationRequests>>,
  TError = void | HTTPValidationError,
>(
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUserNotificationRequests>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUserNotificationRequests>>,
          TError,
          Awaited<ReturnType<typeof getUserNotificationRequests>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUserNotificationRequests<
  TData = Awaited<ReturnType<typeof getUserNotificationRequests>>,
  TError = void | HTTPValidationError,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUserNotificationRequests>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUserNotificationRequests>>,
          TError,
          Awaited<ReturnType<typeof getUserNotificationRequests>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUserNotificationRequests<
  TData = Awaited<ReturnType<typeof getUserNotificationRequests>>,
  TError = void | HTTPValidationError,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUserNotificationRequests>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get User Notification Requests
 */

export function useGetUserNotificationRequests<
  TData = Awaited<ReturnType<typeof getUserNotificationRequests>>,
  TError = void | HTTPValidationError,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUserNotificationRequests>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetUserNotificationRequestsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Retrieves a preview of the current cohort for a given user, including up to 5 member avatars.
 * @summary Get User My Cohort Preview
 */
export const getUserMyCohortPreview = (signal?: AbortSignal) => {
  return getUserMyCohortPreviewMutator<MyCohortPreviewResponse>({
    url: `${import.meta.env['VITE_API_HOST']}/cohorts/preview-my-cohort`,
    method: 'GET',
    signal,
  });
};

export const getGetUserMyCohortPreviewQueryKey = () => {
  return [`${import.meta.env['VITE_API_HOST']}/cohorts/preview-my-cohort`] as const;
};

export const getGetUserMyCohortPreviewQueryOptions = <
  TData = Awaited<ReturnType<typeof getUserMyCohortPreview>>,
  TError = void | HTTPValidationError,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof getUserMyCohortPreview>>, TError, TData>
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetUserMyCohortPreviewQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getUserMyCohortPreview>>> = ({ signal }) =>
    getUserMyCohortPreview(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getUserMyCohortPreview>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetUserMyCohortPreviewQueryResult = NonNullable<
  Awaited<ReturnType<typeof getUserMyCohortPreview>>
>;
export type GetUserMyCohortPreviewQueryError = void | HTTPValidationError;

export function useGetUserMyCohortPreview<
  TData = Awaited<ReturnType<typeof getUserMyCohortPreview>>,
  TError = void | HTTPValidationError,
>(
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUserMyCohortPreview>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUserMyCohortPreview>>,
          TError,
          Awaited<ReturnType<typeof getUserMyCohortPreview>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUserMyCohortPreview<
  TData = Awaited<ReturnType<typeof getUserMyCohortPreview>>,
  TError = void | HTTPValidationError,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUserMyCohortPreview>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUserMyCohortPreview>>,
          TError,
          Awaited<ReturnType<typeof getUserMyCohortPreview>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUserMyCohortPreview<
  TData = Awaited<ReturnType<typeof getUserMyCohortPreview>>,
  TError = void | HTTPValidationError,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUserMyCohortPreview>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get User My Cohort Preview
 */

export function useGetUserMyCohortPreview<
  TData = Awaited<ReturnType<typeof getUserMyCohortPreview>>,
  TError = void | HTTPValidationError,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUserMyCohortPreview>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetUserMyCohortPreviewQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Retrieves the list of cohorts that an existing user is eligible to join as well as the status of the user's last cohort (if any) to support UI display logic. Cohorts that have recently closed within the past 14 days are included in the list to support UI but are filtered out from API response for the rejoin logic.
 * @summary Get User Rejoin Data
 */
export const getUserRejoinData = (signal?: AbortSignal) => {
  return getUserRejoinDataMutator<UserRejoinDataResponse>({
    url: `${import.meta.env['VITE_API_HOST']}/cohorts/rejoin`,
    method: 'GET',
    signal,
  });
};

export const getGetUserRejoinDataQueryKey = () => {
  return [`${import.meta.env['VITE_API_HOST']}/cohorts/rejoin`] as const;
};

export const getGetUserRejoinDataQueryOptions = <
  TData = Awaited<ReturnType<typeof getUserRejoinData>>,
  TError = void | HTTPValidationError,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserRejoinData>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetUserRejoinDataQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getUserRejoinData>>> = ({ signal }) =>
    getUserRejoinData(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getUserRejoinData>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetUserRejoinDataQueryResult = NonNullable<
  Awaited<ReturnType<typeof getUserRejoinData>>
>;
export type GetUserRejoinDataQueryError = void | HTTPValidationError;

export function useGetUserRejoinData<
  TData = Awaited<ReturnType<typeof getUserRejoinData>>,
  TError = void | HTTPValidationError,
>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserRejoinData>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUserRejoinData>>,
          TError,
          Awaited<ReturnType<typeof getUserRejoinData>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUserRejoinData<
  TData = Awaited<ReturnType<typeof getUserRejoinData>>,
  TError = void | HTTPValidationError,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserRejoinData>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUserRejoinData>>,
          TError,
          Awaited<ReturnType<typeof getUserRejoinData>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUserRejoinData<
  TData = Awaited<ReturnType<typeof getUserRejoinData>>,
  TError = void | HTTPValidationError,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserRejoinData>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get User Rejoin Data
 */

export function useGetUserRejoinData<
  TData = Awaited<ReturnType<typeof getUserRejoinData>>,
  TError = void | HTTPValidationError,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserRejoinData>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetUserRejoinDataQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}
